import React from 'react';
import { PageHeader } from '../components/PageHeader';
import { SectionPager } from '../components/SectionPager';
import { Reveal } from '../components/Reveal';
import { metrics } from '../data/metrics';
interface Outcome {
  result: string;
  organization: string;
  detail: string;
}
const outcomes: Outcome[] = [{
  result: '$60M → $270M+',
  organization: 'Ascential Software · DataStage',
  detail: 'Scaled annual DataStage revenue through product innovation, roadmap discipline, and market expansion.'
}, {
  result: '$270M → $600M+',
  organization: 'IBM InfoSphere',
  detail: 'Grew portfolio revenue post-acquisition while directing a global organization of 20+ Product Managers.'
}, {
  result: '8 acquisitions',
  organization: 'Syncsort & Trillium Software',
  detail: 'Defined acquisition product strategy and led technical due diligence as the company tripled in size.'
}, {
  result: '$2M+ in year one',
  organization: 'Syncsort · Hadoop portfolio',
  detail: 'Launched Big Data products into a new market segment, exceeding first-year bookings targets.'
}, {
  result: '$2M in one quarter',
  organization: 'Global Customer Advisory Board',
  detail: 'Converted structured customer insight into incremental revenue within a single quarter.'
}, {
  result: 'ASP uplift',
  organization: 'Accelerate Learning · STEMscopes',
  detail: 'Re-engineered packaging and pricing while maintaining competitive positioning across the portfolio.'
}, {
  result: '3 sale processes',
  organization: 'Harte Hanks · Information Builders · Accelerate Learning',
  detail: 'Authored and evaluated Confidential Information Memoranda alongside bankers and PE stakeholders.'
}];
export function Impact() {
  return <>
      <PageHeader number="03" title="Executive Impact" blurb="Revenue scale, acquisitions integrated, and measurable portfolio outcomes." data-id="element-311" data-mp="1ep953zvrmd6n" />

      <section className="bg-paper py-20 lg:py-24" data-id="element-312" data-mp="3le1g97len9je">
        <div className="mx-auto grid max-w-shell gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-313" data-mp="3leugrqtrt2t3">
          <div className="lg:col-span-5" data-id="element-314" data-mp="3gkgd714yfpdc">
            <Reveal data-id="element-315" data-mp="3cjwg1xkdslr7">
              <h2 className="font-display text-3xl font-light leading-[1.15] text-navy lg:text-[2.4rem]" data-id="element-316" data-mp="3cl7le1th61kf">
                Transforming complex data &amp; SaaS architectures into scalable growth
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:pt-3" data-id="element-317" data-mp="455r7sv75gr4">
            <Reveal delay={0.06} data-id="element-318" data-mp="45e6fresky5h">
              <p className="text-lg leading-[1.75] text-navy/85" data-id="element-319" data-mp="431mj8ph4dps">
                A career defined by building high-performing product organizations, re-engineering
                pricing strategies, and guiding mid-market and enterprise platforms through
                high-value exits.
              </p>
              <p className="mt-6 text-[15px] leading-[1.8] text-steel" data-id="element-320" data-mp="2ewxlpth5vpwh">
                Operating at the intersection of AI/ML innovation, data integration, and corporate
                strategy, Keith bridges technical vision and enterprise value creation —
                translating architecture decisions into portfolio economics that boards, bankers,
                and private equity sponsors can underwrite.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-y border-navy/10 bg-white" data-id="element-321" data-mp="2njqqa4ezqhz1">
        <dl className="mx-auto grid max-w-shell grid-cols-1 px-6 sm:grid-cols-2 lg:grid-cols-4 lg:px-10" data-id="element-322" data-mp="2nj8lgje9ivfi">
          {metrics.map((metric, i) => <div key={metric.value} className={`py-10 lg:px-8 ${i % 2 === 0 ? 'sm:pr-8' : 'sm:border-l sm:border-navy/10 sm:pl-8'} ${i < metrics.length - 1 ? 'border-b border-navy/10 sm:border-b-0' : ''} ${i < 2 ? 'sm:border-b sm:border-navy/10 lg:border-b-0' : ''} ${i === 2 ? 'lg:border-l lg:border-navy/10 lg:pl-8' : ''} lg:first:pl-0 lg:last:pr-0`} data-id="element-323" data-mp="2xh4vf7vgrkc1" data-mpi={i}>
              <dt className="font-display text-2xl text-navy lg:text-[1.75rem]" data-id="element-324" data-mp="2yly77zs481cr">{metric.value}</dt>
              <dd className="mt-2 max-w-[22ch] text-[13px] leading-relaxed text-steel" data-id="element-325" data-mp="2tmv0mawhln2o">
                {metric.label}
              </dd>
            </div>)}
        </dl>
      </section>

      <section className="bg-paper py-20 lg:py-24" data-id="element-326" data-mp="32jcf9o428336">
        <div className="mx-auto max-w-shell px-6 lg:px-10" data-id="element-327" data-mp="2ev30351cqdl0">
          <Reveal data-id="element-328" data-mp="2eti1938088hc">
            <p className="text-[11px] uppercase tracking-overline text-gold" data-id="element-329" data-mp="2auj2k0cv97yc">Outcomes ledger</p>
            <h2 className="mt-5 max-w-xl font-display text-3xl font-light leading-tight text-navy" data-id="element-330" data-mp="2ole40rl1cmrw">
              Where the numbers came from
            </h2>
          </Reveal>

          <div className="mt-10 overflow-x-auto" data-id="element-331" data-mp="2jt4h7kf7w2tu">
            <table className="w-full min-w-[42rem] border-collapse text-left" data-id="element-332" data-mp="1w4bi5f3atev0">
              <caption className="sr-only" data-id="element-333" data-mp="1r6tikfb68bzo">
                Measurable outcomes by result, organization, and context
              </caption>
              <thead data-id="element-334" data-mp="6mo9sv3gm5nk">
                <tr className="border-y border-navy/15" data-id="element-335" data-mp="6mem5e5w0al1">
                  <th scope="col" className="w-56 py-4 pr-6 text-[11px] font-medium uppercase tracking-overline text-steel" data-id="element-336" data-mp="bfr9lkytybc7">
                    Result
                  </th>
                  <th scope="col" className="w-72 py-4 pr-6 text-[11px] font-medium uppercase tracking-overline text-steel" data-id="element-337" data-mp="2kjnyij96dbh">
                    Organization
                  </th>
                  <th scope="col" className="py-4 text-[11px] font-medium uppercase tracking-overline text-steel" data-id="element-338" data-mp="gekcykkeklyn">
                    Context
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-339" data-mp="316mflrdjpccb">
                {outcomes.map(o => <tr key={o.result + o.organization} className="border-b border-navy/10 align-top" data-id="element-340" data-mp="3645xr2dd699j">
                    <th scope="row" className="py-6 pr-6 font-display text-xl font-light text-navy" data-id="element-341" data-mp="2m0qhfdp9w2tt">
                      {o.result}
                    </th>
                    <td className="py-6 pr-6 text-[13px] uppercase tracking-[0.08em] text-gold" data-id="element-342" data-mp="2d6tv9yw3yqee">
                      {o.organization}
                    </td>
                    <td className="py-6 text-[14px] leading-[1.7] text-steel" data-id="element-343" data-mp="23btobd5m3gd6">{o.detail}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <SectionPager current="/impact" data-id="element-344" data-mp="1ualxfg5fdht4" />
    </>;
}